import type { LabelOutputV1 } from '@/sdk/model/LabelOutputV1';
import type { LabelCategoryOutputV1 } from '@/sdk/model/LabelCategoryOutputV1';
import { randomUUID } from '@/utilities/utilities';
import * as graphql from '@/vantage/graphql';
import { ensureDate } from '@/vantage/utilities';
import { isNil } from 'lodash';
import { sqContextApi } from '@/sdk/api/ContextApi';
import type { ContextLabelOutputV1 } from '@/sdk/model/ContextLabelOutputV1';

interface LabelSearchParams {
  categoryId: string;
  name: string;
  parentId?: string;
}

export async function findLabels(categoryId: string): Promise<LabelOutputV1[]> {
  const labels = await findLabelsInternal(categoryId);
  // Special handling: the backend returns all labels when parentId is null/undefined, but we only want to get the records with no parentId if this is the case
  return labels.filter((l) => !l.parentId);
}

async function findLabelsInternal(
  categoryId: string,
  parentId: string | undefined = undefined,
): Promise<LabelOutputV1[]> {
  // If a parentId is passed, retrieve using both the categoryId and parentId
  const args = parentId ? { categoryId, parentId } : { categoryId };
  const promise = sqContextApi.findLabels(args).then((res) => res.data.labels || []);

  return promise;
}

function equalOrBothNil<T>(value1: T | undefined | null, value2: T | undefined | null): boolean {
  return (isNil(value1) && isNil(value2)) || value1 === value2;
}

/** @exportedfortesting */
export async function findLabelByName({ categoryId, name, parentId }: LabelSearchParams) {
  const labels = await findLabelsInternal(categoryId, parentId);
  return labels.find((label) => equalOrBothNil(label.parentId, parentId) && label.name === name);
}

export const findOrCreateLabel = async (params: LabelSearchParams, retry = false): Promise<LabelOutputV1> => {
  const label = await findLabelByName(params);
  if (label) {
    return label;
  }

  try {
    return await sqContextApi.createLabel({ ...params, icon: undefined }).then(({ data }) => data);
  } catch (e) {
    // Create fails if label was created during attempt to fetch label.
    if (retry) {
      return findOrCreateLabel(params, false);
    } else {
      throw e;
    }
  }
};

async function findCategories(parentId: string | undefined): Promise<LabelCategoryOutputV1[]> {
  return sqContextApi.findLabelCategories({ parentId }).then((res) => res.data.categories || []);
}

export async function findCategory(name: string, parentId: string | undefined = undefined) {
  const categories = await findCategories(parentId);
  return categories.find((category) => equalOrBothNil(category.parentId, parentId) && category.name === name);
}

export async function findOrCreateLabelCategory(name: string, parentId: string | undefined = undefined) {
  const category = await findCategory(name, parentId);
  if (category) {
    return category;
  }

  const { data } = await sqContextApi.createLabelCategory({ name, parentId });
  return data;
}

export async function findCategoryHierarchy(categoryNames: string[]) {
  const categories: LabelCategoryOutputV1[] = [];
  let category: LabelCategoryOutputV1 | undefined;
  let categoryName: string | undefined;
  while (categoryNames.length > 0) {
    [categoryName, ...categoryNames] = categoryNames;
    category = await findCategory(categoryName, category?.id);
    if (category) {
      categories.push(category);
    } else {
      break;
    }
  }
  return categories;
}

interface ContextLabelFromNames {
  itemId: string;
  datumId: string;
  categories: any[];
  labelNames: string[];
  start: string;
  end: string;
  metadata?: Record<string, any>;
}

interface ContextLabel {
  itemId: string;
  datumId: string;
  label?: LabelOutputV1;
  start: string;
  end: string;
}

async function createContextLabelSafe({
  itemId,
  datumId,
  label,
  start,
  end,
}: ContextLabel): Promise<ContextLabelOutputV1> {
  try {
    const { data: contextLabel } = await sqContextApi.createContextLabel(
      { datumId, labelId: label?.id, startTime: start, endTime: end },
      { itemId },
    );
    return contextLabel;
  } catch (e: any) {
    // If the context label exists, likely by someone manually creating it without metadata, use it.
    if (e.response?.status === 400 && e.response.data.statusMessage.includes('already exists')) {
      console.warn('Attempted to create a context that already exists. Using existing context instead.');
      const { labels: contextLabels } = await graphql.labelContextForIdPairs([[itemId, datumId]]);
      const contextLabel = contextLabels.find(
        (ctx) =>
          ctx.context === label?.id &&
          ensureDate(ctx.startTime!)?.getTime() === ensureDate(start)?.getTime() &&
          ensureDate(ctx.endTime!)?.getTime() === ensureDate(end)?.getTime() &&
          ctx.archived === false,
      );
      return contextLabel as ContextLabelOutputV1;
    } else {
      throw e;
    }
  }
}

export async function add({ itemId, datumId, categories, labelNames, start, end, metadata }: ContextLabelFromNames) {
  let label: LabelOutputV1 | undefined;
  let category, labelName;
  while (labelNames.length > 0) {
    [category, ...categories] = categories;
    [labelName, ...labelNames] = labelNames;
    label = await findOrCreateLabel({ categoryId: category.id, name: labelName, parentId: label?.id });
    const contextLabel = await createContextLabelSafe({ itemId, datumId, label, start, end });
    await sqContextApi.createContextOpaque(
      {
        datumId: datumId ?? undefined,
        key: `vantage_metadata${randomUUID()}`,
        data: JSON.stringify({ ...metadata, category, labelName, seeq_context_ids: [contextLabel.contextId] }),
        startTime: start ?? undefined,
        endTime: end ?? undefined,
      },
      {
        itemId,
      },
    );
  }
}
